import { defineModule } from '@/js/utils/helpers';

/**
 * The testimonial carousel loops endlessly: the deck is rendered three times and
 * the position lives in the middle copy, so stepping either way always has a
 * card to move to and the arrows never need disabling. After each slide settles
 * the position snaps back into the middle copy with transitions off, which is
 * invisible because every copy is identical.
 *
 * Drag and swipe advance on 70px or a quick flick; `autoplay` rotates the deck
 * on a timer and pauses under the pointer.
 */

const GAP = 34;
const SLIDE_DURATION = 480;

type Carousel = {
  root: HTMLElement;
  track: HTMLElement;
  slides: HTMLElement[];
  count: number;
  autoplay: number;
  position: number;
  pitch: number;
  timer: ReturnType<typeof setTimeout> | null;
  snapTimer: ReturnType<typeof setTimeout> | null;
  pointerId: number | null;
  startX: number;
  startTime: number;
  delta: number;
  dragging: boolean;
};

const carousels: Carousel[] = [];
const cleanups: (() => void)[] = [];

const modulo = (n: number, m: number) => ((n % m) + m) % m;

/** Read a layout property to force a style recalc before transitions come back. */
const forceReflow = (element: HTMLElement) =>
  element.getBoundingClientRect().width;

const render = (carousel: Carousel, animate: boolean) => {
  const { track, slides, position, pitch, count, delta } = carousel;

  track.style.transition = animate
    ? `transform ${SLIDE_DURATION}ms var(--ip-ease-out)`
    : 'none';
  track.style.transform = `translate3d(${-(position + count) * pitch + delta}px, 0, 0)`;

  slides.forEach((slide) => {
    const isActive = Number(slide.dataset.carouselSlide) === position;
    slide.classList.toggle('is-active', isActive);
    slide.toggleAttribute('aria-hidden', !isActive);
  });
};

const measure = (carousel: Carousel) => {
  const first = carousel.slides[0];
  if (first) carousel.pitch = first.offsetWidth + GAP;
};

/* Recentre into the middle copy without animating. `.is-snapping` silences the
   cards' own transition too, because the snap moves `is-active` to another
   element and that element would otherwise restart its scale. */
const snap = (carousel: Carousel) => {
  if (carousel.snapTimer) {
    clearTimeout(carousel.snapTimer);
    carousel.snapTimer = null;
  }
  if (carousel.position >= 0 && carousel.position < carousel.count) return;

  carousel.position = modulo(carousel.position, carousel.count);
  carousel.track.classList.add('is-snapping');
  render(carousel, false);
  // The snap itself must not animate, so let the browser commit it first.
  forceReflow(carousel.track);
  carousel.track.classList.remove('is-snapping');
};

/** Snap back into the middle copy once the slide has settled. */
const scheduleSnap = (carousel: Carousel) => {
  if (carousel.snapTimer) clearTimeout(carousel.snapTimer);
  if (carousel.position >= 0 && carousel.position < carousel.count) return;

  carousel.snapTimer = setTimeout(() => {
    snap(carousel);
    render(carousel, true);
  }, SLIDE_DURATION + 20);
};

/* Snap before stepping, not only on a timer: clicking faster than the slide
   would otherwise walk `position` clean past the tripled deck. */
const advance = (carousel: Carousel, step: number) => {
  snap(carousel);
  carousel.position += step;
  render(carousel, true);
  scheduleSnap(carousel);
};

const scheduleAutoplay = (carousel: Carousel) => {
  if (carousel.timer) clearTimeout(carousel.timer);
  if (!carousel.autoplay || carousel.count < 2) return;

  carousel.timer = setTimeout(() => {
    advance(carousel, 1);
    scheduleAutoplay(carousel);
  }, carousel.autoplay);
};

const goTo = (carousel: Carousel, step: number) => {
  if (carousel.count < 2) return;
  advance(carousel, step);
  scheduleAutoplay(carousel);
};

const onResize = () =>
  carousels.forEach((carousel) => {
    measure(carousel);
    render(carousel, false);
  });

const setup = (root: HTMLElement) => {
  const track = root.querySelector<HTMLElement>('[data-carousel-track]');
  if (!track) return;

  const carousel: Carousel = {
    root,
    track,
    slides: Array.from(
      track.querySelectorAll<HTMLElement>('[data-carousel-slide]'),
    ),
    count: Number(root.dataset.carouselCount) || 1,
    autoplay: Number(root.dataset.carouselAutoplay) || 0,
    position: 0,
    pitch: 0,
    timer: null,
    snapTimer: null,
    pointerId: null,
    startX: 0,
    startTime: 0,
    delta: 0,
    dragging: false,
  };

  measure(carousel);
  render(carousel, false);
  carousels.push(carousel);

  const prev = root.querySelector<HTMLElement>('[data-carousel-prev]');
  const next = root.querySelector<HTMLElement>('[data-carousel-next]');

  const onPrev = () => goTo(carousel, -1);
  const onNext = () => goTo(carousel, 1);
  prev?.addEventListener('click', onPrev);
  next?.addEventListener('click', onNext);

  const onEnter = () => carousel.timer && clearTimeout(carousel.timer);
  const onLeave = () => scheduleAutoplay(carousel);
  root.addEventListener('mouseenter', onEnter);
  root.addEventListener('mouseleave', onLeave);

  const onPointerDown = (event: PointerEvent) => {
    if (carousel.count < 2) return;
    carousel.pointerId = event.pointerId;
    carousel.startX = event.clientX;
    carousel.startTime = performance.now();
    carousel.delta = 0;
    carousel.dragging = false;
    track.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (carousel.pointerId !== event.pointerId) return;
    const delta = event.clientX - carousel.startX;
    if (!carousel.dragging && Math.abs(delta) < 6) return;

    carousel.dragging = true;
    carousel.delta = delta;
    track.classList.add('is-dragging');
    render(carousel, false);
  };

  const onPointerUp = (event: PointerEvent) => {
    if (carousel.pointerId !== event.pointerId) return;

    const delta = event.clientX - carousel.startX;
    const flick =
      Math.abs(delta) > 24 && performance.now() - carousel.startTime < 260;
    const dragged = carousel.dragging;

    carousel.pointerId = null;
    carousel.delta = 0;
    carousel.dragging = false;
    track.classList.remove('is-dragging');

    if (dragged && (Math.abs(delta) > 70 || flick)) {
      goTo(carousel, delta < 0 ? 1 : -1);
    } else {
      render(carousel, true);
    }
  };

  track.addEventListener('pointerdown', onPointerDown);
  track.addEventListener('pointermove', onPointerMove);
  track.addEventListener('pointerup', onPointerUp);
  track.addEventListener('pointercancel', onPointerUp);
  track.addEventListener('dragstart', (event) => event.preventDefault());

  scheduleAutoplay(carousel);

  cleanups.push(() => {
    prev?.removeEventListener('click', onPrev);
    next?.removeEventListener('click', onNext);
    root.removeEventListener('mouseenter', onEnter);
    root.removeEventListener('mouseleave', onLeave);
    track.removeEventListener('pointerdown', onPointerDown);
    track.removeEventListener('pointermove', onPointerMove);
    track.removeEventListener('pointerup', onPointerUp);
    track.removeEventListener('pointercancel', onPointerUp);
    if (carousel.timer) clearTimeout(carousel.timer);
    if (carousel.snapTimer) clearTimeout(carousel.snapTimer);
  });
};

export default defineModule(
  () => {
    document
      .querySelectorAll<HTMLElement>('[data-carousel]')
      .forEach((root) => setup(root));

    if (carousels.length) window.addEventListener('resize', onResize);
  },
  () => {
    cleanups.forEach((cleanup) => cleanup());
    cleanups.length = 0;
    carousels.length = 0;
    window.removeEventListener('resize', onResize);
  },
);
